import { type CSSProperties } from 'react'
import { css, type CSSObject, keyframes, type SerializedStyles } from '@emotion/react'
import { buttonBaseClasses, iconButtonClasses, touchRippleClasses } from '@mui/material'
import { type Theme } from '@mui/material/styles'
import { rem, size } from 'polished'

import { typographyOverrides } from '@Material/typography'
import { highContrast } from '@Styles/a11y'
import { getFocusVisibleIconButtonStyle } from '@Styles/focus'

export const prefix = (theme: Theme, disabled?: boolean): SerializedStyles =>
  css({
    color: theme.palette.text[disabled ? 'disabled' : 'secondary'],
    marginRight: theme.spacing(0),
  })

const visible: CSSObject = {
  position: 'initial',
  zIndex: 1,
}

export const placeIcon = (theme: Theme): SerializedStyles =>
  css({
    fill: theme.palette.text.primary,
  })

export const inputPlace = (theme: Theme, disabled: boolean | undefined): SerializedStyles =>
  css({
    ':focus-within [class*=MuiInputAdornment-positionEnd]': visible,
    '[class*=Mui-disabled]': {
      backgroundColor: 'inherit',
      borderColor: 'inherit',
    },
    '[class*=MuiInputBase-input]': {
      color: theme.palette.text[disabled ? 'disabled' : 'primary'],
      height: rem(44),
      padding: 0,
      textOverflow: 'ellipsis',
    },
    caretColor: theme.palette.primary.main,
    color: theme.palette.text[disabled ? 'disabled' : 'secondary'],
    width: '100%',
    ...(typographyOverrides(theme)?.body2 as CSSProperties),
  })

export const suffix = css({
  ':hover': {
    ...visible,
  },
  outline: 'none',
  zIndex: -1,
})

export const buttonFocus = (theme: Theme, isFocused: boolean | undefined): SerializedStyles =>
  css({
    ...(isFocused && {
      '&:focus-visible::before': getFocusVisibleIconButtonStyle(theme.palette.primary.dark, `-${rem(7)}`),
    }),
  })

export const removeButton = (theme: Theme): SerializedStyles =>
  css({
    '&:focus-visible::before': getFocusVisibleIconButtonStyle(theme.palette.primary.dark, rem(-4)),
    ':hover, :focus, :active': {
      '& svg': {
        fill: theme.palette.primary.dark,
      },
      backgroundColor: 'transparent',
    },
    backgroundColor: theme.palette.text.secondary,
    padding: 0,
    svg: {
      ...size(rem(24)),
      fill: theme.customPalette.background.default,
      // On triche avec un margin négatif pour pouvoir mettre un background sur le bouton
      // qui ne dépasse pas du svg et contrer son padding interne
      margin: rem(-3),
    },
    ...size(rem(18)),
  })

export const swapButton: (theme: Theme) => SerializedStyles = (theme) => {
  const backgroundColor = theme.palette.primary.main
  const OUTLINE_CONTENT_CLIP = '4px'
  const OUTLINE_BORDER_SIZE = '2px'

  return css({
    backgroundColor,
    padding: 0,
    ...size(rem(40)),
    [`&.${iconButtonClasses.root}.${iconButtonClasses.disabled}`]: {
      backgroundColor,
    },
    [`&.${iconButtonClasses.root}.${buttonBaseClasses.focusVisible} .${touchRippleClasses.root}`]: {
      display: 'none',
    },
    '&::after': {
      borderRadius: 'inherit',
      content: '""',
      position: 'absolute',
      ...size(`calc(100% - ${OUTLINE_CONTENT_CLIP})`),
    },
    '&:focus-visible': {
      boxShadow: 'none',
    },
    '&:focus-visible::after': {
      border: `${OUTLINE_BORDER_SIZE} solid ${theme.palette.primary.contrastText}`,
    },
    '&:hover, &:active, &:focus-visible': {
      '& svg': {
        fill: theme.palette.primary.contrastText,
      },
      backgroundColor: theme.palette.primary.dark,
    },
  })
}

export const swapIcon: (
  theme: Theme,
  animationPlayState: AnimationPlayState,
  isHorizontal?: boolean,
  disabled?: boolean,
) => SerializedStyles = (theme, animationPlayState, isHorizontal, disabled) => {
  const initialRotation = isHorizontal ? 0 : 90
  const rotate = keyframes({
    from: { transform: `rotate(${initialRotation}deg)` },
    to: { transform: `rotate(${initialRotation + 180}deg)` },
  })

  return css(
    highContrast({
      fill: 'currentColor',
    }),
    {
      animation: `${rotate} 0.5s ${animationPlayState}`,
      fill: disabled ? theme.palette.text.disabled : theme.palette.primary.contrastText,
      transform: `rotate(${initialRotation}deg)`,
      ...size(rem(24)),
    },
  )
}

export const autocompleteResult = (theme: Theme, isHighlighted: boolean): SerializedStyles =>
  css(
    ...(isHighlighted
      ? [
          highContrast({ border: 'solid 2px transparent' }),
          { backgroundColor: theme.customPalette.onElements.selected },
        ]
      : []),
    {
      // TODO: gap: theme.spacing(2),
      '& > * + *': {
        marginLeft: theme.spacing(2),
      },
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      outline: 'none',
      padding: theme.spacing(1, 2),
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(1, 3),
      },
    },
  )

export const autocompleteResultPlaceIconContainer = css({
  flexShrink: 0,
  ...size(rem(24)),
})

export const autocompleteResultPlaceData = css({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
})

export const placeName = css({
  textTransform: 'capitalize',
})

export const autocompleteResultLinesContainer = (theme: Theme): SerializedStyles =>
  css({
    '& > span, svg': {
      margin: 0,
      marginLeft: theme.spacing(1),
    },
    '& > svg': {
      verticalAlign: 'middle',
      width: rem(20),
    },
    marginLeft: `-${theme.spacing(1)}`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  })
